// src/hooks/usePrinters.js
import { useState, useEffect, useCallback } from "react";
import printerService from "../services/printerService";
import printService from "../services/printService";
import kitchenStationService from "../services/kitchenStationService";
import { toast } from "react-hot-toast";

/**
 * usePrinters Hook
 * Handles fetching, creating, updating, and deleting printer configurations
 * Supports both Server Print and Print Agent modes
 */
export const usePrinters = () => {
  const [printers, setPrinters] = useState([]);
  const [kitchenStations, setKitchenStations] = useState([]);
  const [systemPrinters, setSystemPrinters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSystemPrinters, setLoadingSystemPrinters] = useState(false);

  // Print Agent state
  const [printAgentStatus, setPrintAgentStatus] = useState({
    available: false,
    checking: true,
    version: null,
    printers: [],
  });
  const [printAgentPrinters, setPrintAgentPrinters] = useState([]);

  /** Fetch all printers (including inactive for admin panel) */
  const fetchPrinters = useCallback(async () => {
    setLoading(true);
    try {
      // Use getAllForAdmin to fetch all printers including inactive ones
      const response = await printerService.getAllForAdmin();

      // Handle flexible API response structures
      const data =
        Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

      if (!Array.isArray(data)) {
        console.warn("Unexpected response format:", response);
        setPrinters([]);
        return;
      }

      setPrinters(data);
    } catch (error) {
      console.error("Failed to fetch printers:", error);
      toast.error("Failed to fetch printers");
      setPrinters([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /** Fetch kitchen stations for dropdown */
  const fetchKitchenStations = useCallback(async () => {
    try {
      const response = await kitchenStationService.getAll();
      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];
      setKitchenStations(data.filter(s => s.isActive));
    } catch (error) {
      console.error("Failed to fetch kitchen stations:", error);
      setKitchenStations([]);
    }
  }, []);

  /** Check Print Agent status */
  const checkPrintAgent = useCallback(async () => {
    setPrintAgentStatus(prev => ({ ...prev, checking: true }));

    // Reset the cached availability check so it makes a fresh request
    printService.resetPrintAgentCheck();

    try {
      const status = await printService.getPrintAgentStatus();
      const isAvailable = status?.status === 'running';

      let agentPrinters = [];
      if (isAvailable) {
        const printersResponse = await printService.getPrintAgentPrinters();
        agentPrinters = printersResponse?.printers || [];
      }

      setPrintAgentStatus({
        available: isAvailable,
        checking: false,
        version: status?.version || null,
        printers: agentPrinters,
      });
      setPrintAgentPrinters(agentPrinters);

      // Show feedback to user
      if (isAvailable) {
        toast.success(`Print Agent connected on port ${printService.getPrintAgentPort()}`);
      } else {
        toast.error(`Print Agent not found on port ${printService.getPrintAgentPort()}`);
      }

      return isAvailable;
    } catch (error) {
      console.error("Failed to check Print Agent:", error);
      setPrintAgentStatus({
        available: false,
        checking: false,
        version: null,
        printers: [],
      });
      setPrintAgentPrinters([]);
      toast.error(`Failed to connect: ${error.message}`);
      return false;
    }
  }, []);

  /** Save printer to Print Agent */
  const savePrintAgentPrinter = async (printerData) => {
    try {
      const response = await printService.savePrintAgentPrinter(printerData);
      if (response?.success) {
        toast.success("Printer saved to Print Agent");
        await checkPrintAgent(); // Refresh printer list
        return response;
      } else {
        toast.error(response?.error || "Failed to save printer");
        throw new Error(response?.error);
      }
    } catch (error) {
      console.error("Failed to save Print Agent printer:", error);
      toast.error("Failed to save printer to Print Agent");
      throw error;
    }
  };

  /** Delete printer from Print Agent */
  const deletePrintAgentPrinter = async (printerId) => {
    try {
      const response = await printService.deletePrintAgentPrinter(printerId);
      if (response?.success) {
        toast.success("Printer removed from Print Agent");
        await checkPrintAgent();
        return response;
      } else {
        toast.error(response?.error || "Failed to delete printer");
        throw new Error(response?.error);
      }
    } catch (error) {
      console.error("Failed to delete Print Agent printer:", error);
      toast.error("Failed to remove printer from Print Agent");
      throw error;
    }
  };

  /** Test Print Agent printer */
  const testPrintAgentPrinter = async (printerId) => {
    try {
      const response = await printService.testPrintAgentPrinter(printerId);
      if (response?.success) {
        toast.success("Test print sent successfully!");
      } else {
        toast.error(response?.error || "Test print failed");
      }
      return response;
    } catch (error) {
      console.error("Test print failed:", error);
      toast.error("Test print failed");
      throw error;
    }
  };

  /** Discover network printers via Print Agent */
  const discoverPrinters = async () => {
    try {
      toast.loading("Scanning network for printers...", { id: "discover" });
      const response = await printService.discoverPrinters();
      toast.dismiss("discover");
      if (response?.success && response?.printers?.length > 0) {
        toast.success(`Found ${response.printers.length} printer(s)`);
      } else if (response?.success) {
        toast.error("No printers found on network");
      } else {
        toast.error(response?.error || "Discovery failed");
      }
      return response?.printers || [];
    } catch (error) {
      toast.dismiss("discover");
      console.error("Printer discovery failed:", error);
      toast.error("Failed to discover printers");
      return [];
    }
  };

  /** Get Windows printers via Print Agent */
  const getWindowsPrinters = async () => {
    try {
      toast.loading("Fetching Windows printers...", { id: "windows-printers" });
      const response = await printService.getWindowsPrinters();
      toast.dismiss("windows-printers");
      if (response?.success && response?.printers?.length > 0) {
        toast.success(`Found ${response.printers.length} Windows printer(s)`);
        return response.printers;
      } else if (response?.success) {
        toast.error("No Windows printers found");
        return [];
      } else {
        toast.error(response?.error || "Failed to get Windows printers");
        return [];
      }
    } catch (error) {
      toast.dismiss("windows-printers");
      console.error("Failed to get Windows printers:", error);
      toast.error("Failed to get Windows printers");
      return [];
    }
  };

  /** Fetch system printers from server */
  const fetchSystemPrinters = useCallback(async () => {
    setLoadingSystemPrinters(true);
    try {
      const response = await printerService.getSystemPrinters();
      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];
      setSystemPrinters(data);
    } catch (error) {
      console.error("Failed to fetch system printers:", error);
      setSystemPrinters([]);
    } finally {
      setLoadingSystemPrinters(false);
    }
  }, []);

  /** Create new printer */
  const createPrinter = async (data) => {
    try {
      await printerService.create(data);
      toast.success("Printer created successfully");
      await fetchPrinters();
    } catch (error) {
      console.error("Failed to create printer:", error);
      toast.error("Failed to create printer");
      throw error;
    }
  };

  /** Update existing printer */
  const updatePrinter = async (id, data) => {
    try {
      await printerService.update(id, data);
      toast.success("Printer updated successfully");
      await fetchPrinters();
    } catch (error) {
      console.error(`Failed to update printer ${id}:`, error);
      toast.error("Failed to update printer");
      throw error;
    }
  };

  /** Delete printer */
  const deletePrinter = async (id) => {
    try {
      await printerService.delete(id);
      toast.success("Printer deleted");
      setPrinters((prev) => prev.filter((p) => p.id !== id));
    } catch (error) {
      console.error(`Failed to delete printer ${id}:`, error);
      toast.error("Failed to delete printer");
      throw error;
    }
  };

  /** Activate printer */
  const activatePrinter = async (id) => {
    try {
      await printerService.activate(id);
      toast.success("Printer activated");
      await fetchPrinters();
    } catch (error) {
      console.error(`Failed to activate printer ${id}:`, error);
      toast.error("Failed to activate printer");
      throw error;
    }
  };

  /** Deactivate printer */
  const deactivatePrinter = async (id) => {
    try {
      await printerService.deactivate(id);
      toast.success("Printer deactivated");
      await fetchPrinters();
    } catch (error) {
      console.error(`Failed to deactivate printer ${id}:`, error);
      toast.error("Failed to deactivate printer");
      throw error;
    }
  };

  /** Set printer as default */
  const setDefaultPrinter = async (id) => {
    try {
      await printerService.setDefault(id);
      toast.success("Set as default printer");
      await fetchPrinters();
    } catch (error) {
      console.error(`Failed to set default printer ${id}:`, error);
      toast.error("Failed to set default printer");
      throw error;
    }
  };

  /** Test print */
  const testPrint = async (printerType) => {
    try {
      const response = await printService.testPrint(printerType);
      if (response?.success) {
        toast.success("Test print sent successfully!");
      } else {
        toast.error(response?.message || "Test print failed");
      }
      return response;
    } catch (error) {
      console.error("Test print failed:", error);
      toast.error("Test print failed");
      throw error;
    }
  };

  /** Auto-fetch on mount */
  useEffect(() => {
    fetchPrinters();
    fetchKitchenStations();
    checkPrintAgent(); // Check Print Agent status on mount
  }, [fetchPrinters, fetchKitchenStations, checkPrintAgent]);

  const refreshData = () => {
    fetchPrinters();
    fetchKitchenStations();
    checkPrintAgent();
  };

  return {
    // Server printers
    printers,
    kitchenStations,
    systemPrinters,
    loading,
    loadingSystemPrinters,
    fetchPrinters,
    fetchKitchenStations,
    fetchSystemPrinters,
    createPrinter,
    updatePrinter,
    deletePrinter,
    activatePrinter,
    deactivatePrinter,
    setDefaultPrinter,
    testPrint,
    refreshData,

    // Print Agent
    printAgentStatus,
    printAgentPrinters,
    checkPrintAgent,
    savePrintAgentPrinter,
    deletePrintAgentPrinter,
    testPrintAgentPrinter,
    discoverPrinters,
    getWindowsPrinters,
  };
};

export default usePrinters;
